/**
 * Lazy mermaid rendering. Mermaid is heavy (~500KB), so we import it on demand
 * the first time a diagram appears — keeping the app-shell bundle small and
 * offline-cheap. Runs with `securityLevel: 'strict'` so diagram source can't
 * inject markup.
 */
import { t } from '../i18n';
import { mermaidThemes } from '../generated/mermaid-theme';

let mermaidPromise: Promise<typeof import('mermaid').default> | null = null;

function isDark(): boolean {
  return document.documentElement.dataset.theme === 'dark';
}

/** Brand-generated mermaid config for the active theme (packages/brand). */
function brandMermaidConfig() {
  const palette = isDark() ? mermaidThemes.dark : mermaidThemes.light;
  return {
    startOnLoad: false as const,
    securityLevel: 'strict' as const,
    theme: palette.theme,
    themeVariables: palette.themeVariables,
  };
}

async function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => {
      m.default.initialize(brandMermaidConfig());
      return m.default;
    });
  }
  return mermaidPromise;
}

/** Set on a node that could not be drawn offline; holds its diagram source. */
const PENDING_SOURCE = 'mermaidPendingSource';

/**
 * Show why a diagram is missing offline, keep its source, and retry once the
 * connection returns.
 *
 * Rare diagram types are not precached (see vite.config.ts). Offline, mermaid
 * does not throw when such a type's code cannot be fetched — it draws its own
 * "Syntax error" graphic, which tells the writer their diagram is wrong when it
 * is merely not downloaded, and marks the node done so it never renders again.
 * A diagram that really is invalid shows mermaid's actual error on the retry.
 */
function deferUntilOnline(root: HTMLElement, node: HTMLElement, source: string): void {
  node.dataset[PENDING_SOURCE] = source;
  node.innerHTML = `<pre class="mb-mermaid-error">${escapeHtml(t('mermaid.offlineType'))}</pre>`;
  delete node.dataset.processed;
  window.addEventListener('online', () => void renderMermaid(root), { once: true });
}

/** Render every unprocessed `.mermaid` node inside `root`. */
export async function renderMermaid(root: HTMLElement): Promise<void> {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>('.mermaid:not([data-processed])'));
  if (nodes.length === 0) return;

  // Mermaid replaces each node's text with an SVG, so its source has to be kept
  // before rendering if a failed attempt is ever to be retried. A node deferred
  // earlier gets its source back first.
  const sources = new Map<HTMLElement, string>();
  for (const node of nodes) {
    const pending = node.dataset[PENDING_SOURCE];
    if (pending !== undefined) {
      node.textContent = pending;
      delete node.dataset[PENDING_SOURCE];
    }
    sources.set(node, node.textContent ?? '');
  }
  let runError: unknown = null;
  try {
    const mermaid = await loadMermaid();
    // Re-apply the brand theme for the active light/dark mode before each run.
    mermaid.initialize(brandMermaidConfig());
    await mermaid.run({ nodes });
  } catch (err) {
    runError = err;
  }

  // Judged by what ended up in each node, not by whether run() threw: mermaid
  // marks a node processed before rendering it, draws its error graphic, and
  // may or may not throw afterwards.
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    for (const node of nodes) {
      const svg = node.querySelector('svg');
      if (!svg || svg.getAttribute('aria-roledescription') === 'error') {
        deferUntilOnline(root, node, sources.get(node) ?? '');
      }
    }
    return;
  }

  if (runError) {
    // A malformed diagram shouldn't blow up the preview.
    for (const node of nodes) {
      if (node.querySelector('svg')) continue;
      node.innerHTML = `<pre class="mb-mermaid-error">${
        runError instanceof Error ? escapeHtml(runError.message) : 'Diagram error'
      }</pre>`;
      node.dataset.processed = 'error';
    }
  }
}

let exportRenderCount = 0;

/**
 * Render one diagram to SVG markup for an export, or null if it does not parse.
 *
 * Always in the light palette, whatever the app theme: the result lands on white
 * paper. Labels are drawn as SVG text rather than HTML in a `foreignObject`,
 * because the export rasterizes the SVG and browsers refuse to read back a
 * canvas that drew a `foreignObject`.
 */
export async function renderMermaidForExport(source: string): Promise<string | null> {
  const mermaid = await loadMermaid();
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    theme: mermaidThemes.light.theme,
    themeVariables: mermaidThemes.light.themeVariables,
    htmlLabels: false,
    flowchart: { htmlLabels: false },
  });
  try {
    exportRenderCount += 1;
    const { svg } = await mermaid.render(`mb-export-diagram-${exportRenderCount}`, source);
    return svg.includes('aria-roledescription="error"') ? null : svg;
  } catch {
    return null;
  } finally {
    // Back to the preview's configuration for the next on-screen render.
    mermaid.initialize(brandMermaidConfig());
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
